import jwtDecode from 'jwt-decode';
import isEqual from 'lodash/isEqual';

import { expValEquals } from '@confluence/feature-experiments';

type cacheWithExp<T> = {
	getValue: (arg?: any) => Promise<T>;
	forceGetValue: (arg?: any) => Promise<T>;
};

type PendingRequest<T> = {
	arg: any;
	promise: Promise<T>;
};

const BUFFER_MS = 60 * 1000; // refresh token 60 seconds before it expires
const EMPTY = [-Infinity, null, null];

/**
 * Memorize the result of fn in timeout milliseconds.
 *
 * When `platform_media_token_nested_macro` is enabled, concurrent non-forced calls with
 * the same arg share a single in-flight request, so a burst of callers (e.g. several
 * media nodes asking for the same token) only triggers one call to `asyncFunction`.
 * Rejections are not cached.
 */
export function cacheWithExp<T>(
	asyncFunction: (any?: any) => Promise<T>,
	tokenOrExpInMsAccessor: (json: T) => number | string,
	tokenExpBufferInMs: number = BUFFER_MS,
): cacheWithExp<T> {
	const cache = {};
	const pending: Record<string, PendingRequest<T>> = {};

	// Original implementation — used when platform_media_token_nested_macro is disabled.
	// Delete this function when the experiment is cleaned up.
	async function getValueLegacy(arg: any, force: boolean): Promise<T> {
		// It is ok to have clashing hash because we are going to check args anyway
		// Worse case is calling fn() more often than it should
		let hash = '';
		try {
			hash = JSON.stringify(arg);
		} catch {}

		const [exp, lastArg, lastValue] = cache[hash] || EMPTY;
		if (force || exp <= Date.now() || !isEqual(lastArg, arg)) {
			const value = await asyncFunction(arg);

			let tokenOrExp: string | number | null = null;
			try {
				tokenOrExp = tokenOrExpInMsAccessor(value);
			} catch (e) {
				if (process.env.NODE_ENV !== 'production') {
					throw e;
				}
			}

			let exp: number | null = null;
			if (tokenOrExp) {
				if (!isNaN(tokenOrExp as any)) {
					exp = Number(tokenOrExp);
				} else if (typeof tokenOrExp === 'string') {
					try {
						exp = jwtDecode<{ exp: number }>(tokenOrExp).exp * 1000 - tokenExpBufferInMs;
					} catch (e) {
						if (process.env.NODE_ENV !== 'production') {
							throw e;
						}
					}
				} else {
					if (process.env.NODE_ENV !== 'production') {
						throw new Error(
							`The return value can only be a string (JWT token) or number (expiry time in ms) but getting: ${tokenOrExp}`,
						);
					}
				}
			}
			cache[hash] = exp === null ? EMPTY : [exp, arg, value];

			return value;
		}

		return lastValue;
	}

	function getExp(value: T): number | null {
		let tokenOrExp: string | number | null = null;
		try {
			tokenOrExp = tokenOrExpInMsAccessor(value);
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				throw e;
			}
		}

		let exp: number | null = null;
		if (tokenOrExp) {
			if (!isNaN(tokenOrExp as any)) {
				exp = Number(tokenOrExp);
			} else if (typeof tokenOrExp === 'string') {
				try {
					exp = jwtDecode<{ exp: number }>(tokenOrExp).exp * 1000 - tokenExpBufferInMs;
				} catch (e) {
					if (process.env.NODE_ENV !== 'production') {
						throw e;
					}
				}
			} else {
				if (process.env.NODE_ENV !== 'production') {
					throw new Error(
						`The return value can only be a string (JWT token) or number (expiry time in ms) but getting: ${tokenOrExp}`,
					);
				}
			}
		}
		return exp;
	}

	async function fetchAndStore(hash: string, arg: any): Promise<T> {
		const value = await asyncFunction(arg);
		const exp = getExp(value);
		cache[hash] = exp === null ? EMPTY : [exp, arg, value];
		return value;
	}

	// New implementation — used when platform_media_token_nested_macro is enabled.
	// Shares identical in-flight requests.
	function getValueWithInFlightSharing(arg: any, force: boolean): Promise<T> {
		// It is ok to have clashing hash because we are going to check args anyway
		// Worse case is calling fn() more often than it should
		let hash = '';
		try {
			hash = JSON.stringify(arg);
		} catch {}

		const [exp, lastArg, lastValue] = cache[hash] || EMPTY;
		if (!force && exp > Date.now() && isEqual(lastArg, arg)) {
			return Promise.resolve(lastValue);
		}

		// Share an identical request that is already in flight instead of firing another one.
		// Forced calls always bypass this, since they exist to get a fresh value.
		const inFlight = pending[hash];
		if (!force && inFlight && isEqual(inFlight.arg, arg)) {
			return inFlight.promise;
		}

		const promise = fetchAndStore(hash, arg).finally(() => {
			// Clear the entry once settled (so rejections and empty tokens are never shared),
			// but only if it is still ours — a later forced call may have replaced it.
			if (pending[hash]?.promise === promise) {
				delete pending[hash];
			}
		});
		pending[hash] = { arg, promise };
		return promise;
	}

	// Evaluated per call (not at creation): cache instances are created at module load,
	// before experiment values are available.
	function getValue(arg: any, force: boolean): Promise<T> {
		return expValEquals('platform_media_token_nested_macro', 'isEnabled', true)
			? getValueWithInFlightSharing(arg, force)
			: getValueLegacy(arg, force);
	}

	return {
		forceGetValue: (arg) => {
			return getValue(arg, true);
		},
		getValue: (arg) => {
			return getValue(arg, false);
		},
	};
}
